import { useState, useEffect, useCallback, useRef } from 'react';
import {
  useCreateInventoryRecordMutation,
  useCreateInventoryRecordWithItemsMutation,
} from '../../../../redux/services/inventoryApi';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../../../redux/slices/auth.slice';
import { ProductsService, LocationsService, InventoryRecordsService } from '../../../../api';
import { ProductsServiceHelpers } from '../../../../utils/api-helpers/ProductsService.helpers';
import {
  CreateInventoryRecordWithItemsDto,
  InventoryRecordItemDto,
  IndividualWarehouseUnitDto,
} from '../../../../api';
import { ProductItem, LocationData, ProductData } from '../types';
import {
  filterProductUnitsToProductItems,
  createDefaultUnitsForProduct,
  lengthMmToCmField,
  catalogFieldToString,
  replaceSectionUnitsInPlace,
  moveSectionUnits,
  productOrderFromUnits,
  sortItemsByProductOrder,
  type MergedProductUnit,
} from '../productUnitsMerge.utils';
import {
  collectHydratedRecordItemIds,
  extractProductIdsFromRecordItems,
  mapRecordItemsToProductItems,
  resolveRecordItemProductDisplay,
  sortRecordItemsByDisplayOrder,
  type RecordItemLike,
} from '../editRecordHydration.utils';

interface AddInventoryPanelProps {
  open: boolean;
  onClose: () => void;
  /** Parent save must return a Promise so the panel can await and block double-submit. */
  onSave?: (data: any) => void | Promise<void>;
  editMode?: boolean;
  recordToEdit?: any;
}

function normalizeProductId(raw: unknown): number | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  if (Number.isNaN(n) || n <= 0) return undefined;
  return n;
}

/**
 * Refleja en `productItems` (usado para hidratación/edición/borrado en backend) el conteo
 * real de piezas de un producto en `productUnits`. `productItems` nunca se edita a mano:
 * siempre es una proyección de las piezas que existen. Sin piezas → la fila desaparece.
 */
function syncItemForProductCount(
  items: ProductItem[],
  ref: { productId: number; productCode: string; productName: string },
  newCount: number,
): ProductItem[] {
  const idx = items.findIndex(
    (it) =>
      (it.productId != null && it.productId === ref.productId) ||
      (it.productCode && it.productCode === ref.productCode),
  );

  if (newCount <= 0) {
    return idx === -1 ? items : items.filter((_, i) => i !== idx);
  }

  if (idx === -1) {
    const newId = items.length > 0 ? Math.max(...items.map((item) => item.id)) + 1 : 1;
    return [
      ...items,
      {
        id: newId,
        productId: ref.productId,
        productName: ref.productName,
        productCode: ref.productCode,
        quantity: String(newCount),
      },
    ];
  }

  return items.map((item, i) => (i === idx ? { ...item, quantity: String(newCount) } : item));
}

/** Convierte un item de /products/defaults/batch al shape ProductData, heredando lo que ya
 *  hubiera en caché para los campos que el batch no traiga. */
function buildProductDataFromDefaultsItem(item: any, existing?: ProductData): ProductData {
  return {
    id: item.id,
    name: item.name ?? existing?.name ?? '',
    code: item.code ?? existing?.code ?? '',
    supplier: existing?.supplier,
    material:
      item.materialId != null
        ? { id: item.materialId, name: item.materialName ?? '' }
        : existing?.material,
    weightGrams:
      item.weightGrams != null ? String(item.weightGrams) : (existing?.weightGrams ?? ''),
    // Keep raw mm (same as fetchProducts); form converts to cm via lengthMmToCmField.
    lengthMm: item.lengthMm != null ? String(item.lengthMm) : (existing?.lengthMm ?? ''),
    diameterSize: item.diameterSize ?? existing?.diameterSize ?? '',
    color: item.color ?? existing?.color ?? '',
    workshopCode:
      typeof item.workshopCode === 'string' ? item.workshopCode : (existing?.workshopCode ?? ''),
    costPrice: item.costPrice != null ? String(item.costPrice) : (existing?.costPrice ?? ''),
    salePrice: item.salePrice != null ? String(item.salePrice) : (existing?.salePrice ?? ''),
  };
}

export const useAddInventoryPanel = ({
  open,
  onClose,
  onSave,
  editMode = false,
  recordToEdit = null,
}: AddInventoryPanelProps) => {
  // Get the current user from Redux state
  const currentUser = useSelector(selectCurrentUser);

  // Replace the query hook with state variables
  const [locationsData, setLocationsData] = useState<LocationData[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState<any>(null);

  const [createInventoryRecord, { isLoading: isSubmitting, isSuccess, error: submitError }] =
    useCreateInventoryRecordMutation();
  const [
    createInventoryRecordWithItems,
    {
      isLoading: isSubmittingWithItems,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      isSuccess: isSuccessWithItems,
      error: submitErrorWithItems,
    },
  ] = useCreateInventoryRecordWithItemsMutation();

  // State for products
  const [productsData, setProductsData] = useState<ProductData[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<any>(null);

  // Panel state
  // currentStep: 1 = pantalla unificada (agregar productos + personalizar piezas), 2 = confirmación
  const [currentStep, setCurrentStep] = useState(1);
  // productItems es una PROYECCIÓN de productUnits (se recalcula en cada add/remove de pieza o
  // sección); nunca se edita directamente. Se mantiene porque el backend y la hidratación de
  // borradores en edición identifican líneas por ella (id de línea, borrados, defaults de catálogo).
  const [productItems, setProductItems] = useState<ProductItem[]>([]);
  // This is actually a location ID from the backend
  const [selectedLocationId, setSelectedLocationId] = useState('');
  // Selected supplier ID
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [showSupplierValidation, setShowSupplierValidation] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');

  // State for save draft confirmation modal
  const [showSaveDraftConfirmModal, setShowSaveDraftConfirmModal] = useState(false);

  // Block double-submit on confirm / draft (ref catches clicks before re-render)
  const [isConfirming, setIsConfirming] = useState(false);
  const confirmingRef = useRef(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const savingDraftRef = useRef(false);

  // State for cancel confirmation (when clicking X)
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // State for error snackbar (draft validation/API errors)
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Store timeout IDs to clear them when component unmounts or panel closes
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  // Track IDs already enriched with defaults to avoid duplicate fetches while panel is open
  const fetchedDefaultsIdsRef = useRef<Set<number>>(new Set());

  // Add state for product units with properties and costs
  const [productUnits, setProductUnits] = useState<any[]>([]);
  /** Live units for async section qty (avoid stale length after await). */
  const productUnitsRef = useRef(productUnits);
  productUnitsRef.current = productUnits;

  /** IDs de líneas de detalle al abrir edición: sirve para enviar `delete: true` si el usuario quita filas. */
  const editLoadedRecordItemIdsRef = useRef<Set<number>>(new Set());

  /**
   * Líneas que el formulario logró hidratar en esta sesión de edición.
   * No enviar `delete: true` para ítems que nunca se mostraron (p. ej. tras refresh sin caché).
   */
  const editHydratedRecordItemIdsRef = useRef<Set<number>>(new Set());

  /**
   * Evita re-hidratar productItems/productUnits desde recordToEdit en cada cambio de productsData
   * (p. ej. fetchMissingProductDefaults), lo que borraba filas nuevas al editar un borrador.
   * Se marca cuando todas las líneas del registro están en el formulario.
   */
  const editFormHydratedRecordIdRef = useRef<number | null>(null);

  // Create a fetch function for locations
  const fetchLocations = async (retryCount = 0) => {
    try {
      setLocationsLoading(true);
      // Use the LocationsService to fetch locations
      const response = await LocationsService.locationsControllerFindAll();

      // Process the response data
      let locationsList: LocationData[] = [];

      if (response && Array.isArray(response.data)) {
        locationsList = response.data
          .filter((item: any) => item.type === 'WAREHOUSE' && item.status === 'ACTIVE')
          .map((item: any) => ({
            id: item.id,
            name: item.name,
            type: item.type,
            status: item.status,
          }));
      }

      setLocationsData(locationsList);
      setLocationsError(null);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setLocationsError(error);

      // Retry up to 2 times with exponential backoff if the request fails
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => {
          fetchLocations(retryCount + 1);
        }, delay);
      }
    } finally {
      setLocationsLoading(false);
    }
  };

  /**
   * Warm a small active-product cache for edit hydration; product selection itself is async/search-based.
   */
  const fetchProducts = async () => {
    try {
      setProductsLoading(true);
      // Keep this bounded. Missing selected products are hydrated by fetchMissingProductDefaults.
      const response = await ProductsService.productsControllerFindAll(
        1, // page
        20, // limit
        undefined, // searchName
        undefined, // searchCode
        undefined, // searchTerm
        undefined, // categoryId
        'ACTIVE', // status - only active products
        undefined, // supplierId
      );

      // Transform the response data into the format needed by the component
      let productsList: ProductData[] = [];

      if (response && Array.isArray(response.data)) {
        productsList = response.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          code: item.code,
          supplier: item.supplier,
          material: item.material,
          weightGrams: item.weightGrams,
          lengthMm: item.lengthMm,
          diameterSize: item.diameterSize,
          color: item.color,
          workshopCode: item.workshopCode,
          costPrice: item.costPrice,
          salePrice: item.salePrice,
        }));
      } else if (Array.isArray(response)) {
        productsList = response.map((item: any) => ({
          id: item.id,
          name: item.name,
          code: item.code,
          supplier: item.supplier,
          material: item.material,
          weightGrams: item.weightGrams,
          lengthMm: item.lengthMm,
          diameterSize: item.diameterSize,
          color: item.color,
          workshopCode: item.workshopCode,
          costPrice: item.costPrice,
          salePrice: item.salePrice,
        }));
      }

      setProductsData(productsList);
      setProductsError(null);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProductsError(error);
    } finally {
      setProductsLoading(false);
    }
  };

  // Reset form when panel opens
  useEffect(() => {
    if (open) {
      fetchLocations();
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Reset the form after locations data is loaded (but not in edit mode)
  useEffect(() => {
    if (open && locationsData.length > 0 && !editMode) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locationsData, editMode]);

  // Reset confirmation modal state when panel opens or closes
  useEffect(() => {
    // Clear all pending timeouts when panel closes or opens
    timeoutRefs.current.forEach((timeout) => clearTimeout(timeout));
    timeoutRefs.current = [];
    if (!open) {
      fetchedDefaultsIdsRef.current.clear();
      editFormHydratedRecordIdRef.current = null;
      editHydratedRecordItemIdsRef.current.clear();
    }

    // Reset confirmation modal state
    setShowConfirmModal(false);
    setConfirmMessage('');
    setShowCancelConfirmation(false);
  }, [open]);

  // Refresca el set de item ids del registro al abrir cierre/edicion (para deletions)
  useEffect(() => {
    if (!open) {
      editLoadedRecordItemIdsRef.current.clear();
      return;
    }
    if (editMode && recordToEdit?.items && Array.isArray(recordToEdit.items)) {
      const ids = (recordToEdit.items as { id?: number }[])
        .map((i) => i.id)
        .filter((id): id is number => id != null && !Number.isNaN(Number(id)) && Number(id) > 0);
      editLoadedRecordItemIdsRef.current = new Set(ids);
    }
  }, [open, editMode, recordToEdit]);

  // Initialize component when it first mounts
  useEffect(() => {
    if (locationsData && locationsData.length > 0) {
      setSelectedLocationId(locationsData[0].id.toString());
    }
  }, [locationsData]);

  // Set default location when locations are loaded or when moving to step 2
  useEffect(() => {
    // Set a default location if none is selected and locations are available
    if (
      locationsData &&
      locationsData.length > 0 &&
      (!selectedLocationId || selectedLocationId === '')
    ) {
      const defaultLocationId = locationsData[0].id.toString();
      setSelectedLocationId(defaultLocationId);
    }
  }, [locationsData, selectedLocationId]);

  // Make sure a location is selected when on step 2
  useEffect(() => {
    if (
      currentStep === 2 &&
      locationsData.length > 0 &&
      (!selectedLocationId || selectedLocationId === '')
    ) {
      setSelectedLocationId(locationsData[0].id.toString());
    }
  }, [currentStep, locationsData, selectedLocationId]);

  // Set supplier ID when in edit mode (needs to run first to trigger products fetch)
  useEffect(() => {
    if (editMode && recordToEdit && open) {
      // Set the supplier ID first - this will trigger products fetch
      if (recordToEdit.supplier?.id || recordToEdit.supplier_id) {
        const supplierId = (recordToEdit.supplier?.id ?? recordToEdit.supplier_id).toString();
        setSelectedSupplierId(supplierId);
      } else {
        console.warn('No supplier found in record to edit');
        setSelectedSupplierId('');
      }

      // Set the branch/location ID (support both branch_id and destinationLocationId)
      const locationId =
        recordToEdit.branch_id ??
        recordToEdit.destinationLocationId ??
        recordToEdit.destinationLocation?.id;
      if (locationId != null) {
        setSelectedLocationId(locationId.toString());
      }

      // Siempre comenzar en el paso 1 al abrir un registro en edición
      setCurrentStep(1);
    }
  }, [editMode, recordToEdit, open]);

  /** Genera piezas por defecto (valores de catálogo) para líneas que no traen individualUnits. */
  const generateDefaultUnitsFromItems = useCallback(
    (items: ProductItem[]): MergedProductUnit[] => {
      return items.flatMap((item) => {
        const productId = normalizeProductId(item.productId);
        if (productId == null) return [];
        const product = productsData.find((p) => p.id === productId);
        const quantity = Math.max(1, parseInt(String(item.quantity), 10) || 1);
        return createDefaultUnitsForProduct({
          productId,
          productName: item.productName,
          productCode: item.productCode,
          quantity,
          product,
          selectedSupplierId,
        });
      });
    },
    [productsData, selectedSupplierId],
  );

  // Load product items when in edit mode (uses item.product from API; does not require warm cache).
  useEffect(() => {
    if (!open) {
      return;
    }

    if (editMode && recordToEdit?.items != null && recordToEdit.items.length > 0) {
      const recordId = Number(recordToEdit.id);
      if (
        editFormHydratedRecordIdRef.current != null &&
        editFormHydratedRecordIdRef.current === recordId
      ) {
        return;
      }

      // Check if record is PENDING
      const isPending = recordToEdit.status === 'PENDING' || recordToEdit.status === 'Pendiente';

      const recordItems = recordToEdit.items as RecordItemLike[];

      const itemsInDisplayOrder = sortRecordItemsByDisplayOrder(recordItems);
      const formattedItems = mapRecordItemsToProductItems(recordItems, productsData);

      if (formattedItems.length > 0) {
        setProductItems(formattedItems);
        editHydratedRecordItemIdsRef.current = collectHydratedRecordItemIds(formattedItems);
      } else {
        console.warn('No valid items could be mapped from the record data');
        editHydratedRecordItemIdsRef.current.clear();
        setProductItems([]);
      }

      const expectedItemCount = itemsInDisplayOrder.length;
      const allRecordItemsMapped =
        expectedItemCount === 0 || formattedItems.length >= expectedItemCount;
      if (allRecordItemsMapped && !Number.isNaN(recordId) && recordId > 0) {
        editFormHydratedRecordIdRef.current = recordId;
      }

      // If PENDING record has individualUnits, load them into productUnits
      if (isPending) {
        const loadedUnits: any[] = [];

        itemsInDisplayOrder.forEach((item: RecordItemLike) => {
          if (item.individualUnits && Array.isArray(item.individualUnits)) {
            const display = resolveRecordItemProductDisplay(item, productsData);
            const productId = display?.productId ?? null;
            const product =
              productId != null ? productsData.find((p) => p.id === productId) : undefined;
            const productCode = display?.productCode ?? '';
            const productName = display?.productName ?? '';

            item.individualUnits.forEach((unit: IndividualWarehouseUnitDto, unitIndex: number) => {
              loadedUnits.push({
                id: `${productCode || 'product'}-${unitIndex + 1}`,
                productId: productId,
                productName: productName,
                productCode: productCode,
                quantity: 1,
                properties: {
                  materialId: unit.materialId
                    ? unit.materialId.toString()
                    : product?.material?.id
                      ? product.material.id.toString()
                      : '',
                  materialName: product?.material?.name || '',
                  weight: unit.weightGrams
                    ? unit.weightGrams.toString()
                    : product?.weightGrams
                      ? product.weightGrams.toString()
                      : '',
                  length: lengthMmToCmField(unit.lengthMm ?? product?.lengthMm),
                  diameter:
                    unit.diameterSize != null && String(unit.diameterSize).trim() !== ''
                      ? String(unit.diameterSize)
                      : product?.diameterSize != null && String(product.diameterSize).trim() !== ''
                        ? String(product.diameterSize)
                        : '',
                  color: unit.color || product?.color || '',
                  workshopCode: unit.workshopCode || product?.workshopCode || '',
                },
                costs: {
                  costPrice: unit.costPrice
                    ? unit.costPrice.toString()
                    : product?.costPrice
                      ? product.costPrice.toString()
                      : '',
                  salePrice: unit.salePrice
                    ? unit.salePrice.toString()
                    : product?.salePrice
                      ? product.salePrice.toString()
                      : '',
                },
              });
            });
          } else {
            console.warn('Item has no individualUnits or it is not an array:', item);
          }
        });

        if (loadedUnits.length > 0) {
          setProductUnits(loadedUnits);
        } else {
          // Pendiente pero sin individualUnits guardadas todavía (p. ej. borrador viejo,
          // pre-piezas): generar piezas por defecto desde catálogo para poblar las secciones.
          setProductUnits(generateDefaultUnitsFromItems(formattedItems));
        }
      } else {
        // Registro no pendiente en edición: no trae individualUnits, generar por defecto.
        setProductUnits(generateDefaultUnitsFromItems(formattedItems));
      }
    }
  }, [editMode, recordToEdit, open, productsData, generateDefaultUnitsFromItems]);

  // When catalog cache warms after edit hydrate, fill empty synthetic defaults once
  // (cold generateDefaultUnitsFromItems) without wiping user edits or re-locking hydrate.
  useEffect(() => {
    if (!open || !editMode || productsData.length === 0) return;

    setProductUnits((prev) => {
      let changed = false;
      const next = prev.map((unit) => {
        const product = productsData.find((p) => p.id === unit.productId);
        if (product == null) return unit;

        const costEmpty = unit.costs?.costPrice == null || unit.costs.costPrice === '';
        const saleEmpty = unit.costs?.salePrice == null || unit.costs.salePrice === '';
        const materialEmpty =
          unit.properties?.materialId == null || unit.properties.materialId === '';
        const weightEmpty = unit.properties?.weight == null || unit.properties.weight === '';
        if (!costEmpty && !saleEmpty && !materialEmpty && !weightEmpty) return unit;

        changed = true;
        return {
          ...unit,
          properties: {
            ...unit.properties,
            materialId:
              materialEmpty && product.material?.id != null
                ? product.material.id.toString()
                : unit.properties.materialId,
            materialName: materialEmpty
              ? (product.material?.name ?? unit.properties.materialName ?? '')
              : unit.properties.materialName,
            weight: weightEmpty
              ? catalogFieldToString(product.weightGrams)
              : unit.properties.weight,
            length:
              unit.properties?.length == null || unit.properties.length === ''
                ? lengthMmToCmField(product.lengthMm)
                : unit.properties.length,
            diameter:
              unit.properties?.diameter == null || unit.properties.diameter === ''
                ? catalogFieldToString(product.diameterSize)
                : unit.properties.diameter,
            color:
              unit.properties?.color == null || unit.properties.color === ''
                ? catalogFieldToString(product.color)
                : unit.properties.color,
            workshopCode:
              unit.properties?.workshopCode == null || unit.properties.workshopCode === ''
                ? catalogFieldToString(product.workshopCode)
                : unit.properties.workshopCode,
          },
          costs: {
            ...unit.costs,
            costPrice: costEmpty ? catalogFieldToString(product.costPrice) : unit.costs.costPrice,
            salePrice: saleEmpty ? catalogFieldToString(product.salePrice) : unit.costs.salePrice,
          },
        };
      });
      return changed ? next : prev;
    });
  }, [open, editMode, productsData]);

  // If there's an error, show it
  useEffect(() => {
    if (submitError) {
      console.error('Error submitting inventory record:', submitError);
      setConfirmMessage('No se pudo crear el registro. Intente de nuevo.');
    }
  }, [submitError]);

  const resetForm = () => {
    setCurrentStep(1);

    // Only reset product items if not in edit mode or if we're closing the panel
    if (!editMode || !open) {
      setProductItems([]);
      // Only reset productUnits when not in edit mode or closing
      setProductUnits([]);
    }

    // Only set the selectedLocation if locations are available, otherwise keep it as is
    if (locationsData && locationsData.length > 0 && (!editMode || !recordToEdit)) {
      setSelectedLocationId(locationsData[0].id.toString());
    }

    // Reset supplier selection if not in edit mode
    if (!editMode || !open) {
      setSelectedSupplierId('');
    }

    setShowConfirmModal(false);
    setConfirmMessage('');
    setShowSupplierValidation(false);
  };

  const handleSupplierChange = useCallback((supplierId: string) => {
    setSelectedSupplierId(supplierId);
    if (supplierId) {
      setShowSupplierValidation(false);
    }
  }, []);

  // Batch-fetch defaults for selected products that are not present in local productsData.
  // This keeps step 2 inheritance robust with large catalogs + async paginated search.
  // Devuelve los ProductData recién construidos: quien llama y necesita usarlos DE INMEDIATO
  // (p. ej. para generar piezas) no debe releer productsData justo después del await, porque
  // el setState de abajo puede no haberse aplicado todavía a ese cierre.
  const fetchMissingProductDefaults = useCallback(
    async (productIds: number[]): Promise<ProductData[]> => {
      if (productIds.length === 0) {
        return [];
      }

      try {
        const response = await ProductsServiceHelpers.getDefaultsBatch(productIds);
        const defaults = Array.isArray(response?.data) ? response.data : [];
        if (defaults.length === 0) {
          return [];
        }

        let built: ProductData[] = [];
        setProductsData((prev) => {
          const byId = new Map(prev.map((p) => [p.id, p]));
          built = defaults.map((item) => buildProductDataFromDefaultsItem(item, byId.get(item.id)));
          built.forEach((p) => byId.set(p.id, p));
          return Array.from(byId.values());
        });

        setProductItems((prev) =>
          prev.map((productItem) => {
            const match = defaults.find((entry) => entry.id === productItem.productId);
            if (!match) {
              return productItem;
            }
            const workshopCode =
              typeof match.workshopCode === 'string' && match.workshopCode.trim()
                ? match.workshopCode.trim()
                : productItem.workshopCode;
            return {
              ...productItem,
              productName: match.name ?? productItem.productName,
              productCode: match.code ?? productItem.productCode,
              workshopCode,
            };
          }),
        );

        return built;
      } catch (error) {
        console.error('Error fetching product defaults in batch:', error);
        return [];
      }
    },
    [],
  );

  /**
   * Devuelve el producto con sus datos de catálogo completos. Si ya está en el caché local
   * (productsData), lo usa tal cual — nunca decide "está incompleto" por sí sola (un producto
   * puede legítimamente no tener material/peso configurado). Si NO está en el caché, lo descarga
   * y espera la respuesta antes de devolver: nunca se generan piezas con datos que existen en el
   * catálogo pero que el panel simplemente no había pedido todavía.
   */
  const ensureFullProductData = useCallback(
    async (
      productId: number,
      fallback: { name: string; code: string; workshopCode?: string },
    ): Promise<ProductData> => {
      const cached = productsData.find((p) => p.id === productId);
      if (cached) return cached;

      const fetched = await fetchMissingProductDefaults([productId]);
      const match = fetched.find((p) => p.id === productId);
      if (match) return match;

      // Falló la descarga (red, timeout, etc.): igual permitir continuar con datos vacíos,
      // nunca bloquear al usuario — puede completar manualmente y reintentar con "Restablecer".
      return {
        id: productId,
        name: fallback.name,
        code: fallback.code,
        workshopCode: fallback.workshopCode,
      };
    },
    [productsData, fetchMissingProductDefaults],
  );

  /**
   * Agrega un producto a la pantalla unificada: crea `quantity` piezas nuevas con valores del
   * catálogo. Si el producto ya tiene una sección abierta, suma a lo que ya existe (no duplica
   * la sección). No afecta ninguna otra sección.
   */
  const addProductToDraft = useCallback(
    async (product: ProductData, quantity: number) => {
      if (!product || quantity <= 0) return;

      // Si el producto no está en el caché local todavía, esperar su data de catálogo ANTES de
      // crear las piezas — nunca crearlas con material/peso/costo en blanco por una carrera con
      // la descarga en curso.
      const fullProduct = await ensureFullProductData(product.id, {
        name: product.name,
        code: product.code,
        workshopCode: product.workshopCode,
      });

      setProductUnits((prevUnits) => {
        const existingCount = prevUnits.filter((u) => u.productId === fullProduct.id).length;
        const newUnits = createDefaultUnitsForProduct({
          productId: fullProduct.id,
          productName: fullProduct.name || product.name,
          productCode: fullProduct.code || product.code,
          quantity,
          product: fullProduct,
          selectedSupplierId,
        });
        setProductItems((prevItems) =>
          syncItemForProductCount(
            prevItems,
            {
              productId: fullProduct.id,
              productCode: fullProduct.code || product.code,
              productName: fullProduct.name || product.name,
            },
            existingCount + quantity,
          ),
        );
        return [...prevUnits, ...newUnits];
      });
    },
    [selectedSupplierId, ensureFullProductData],
  );

  /** Quita una pieza puntual. Sin confirmación: es precisa, sobre una fila que el usuario ve. */
  const removeUnit = useCallback((unitId: string) => {
    setProductUnits((prevUnits) => {
      const target = prevUnits.find((u) => u.id === unitId);
      if (!target) return prevUnits;
      const nextUnits = prevUnits.filter((u) => u.id !== unitId);
      const remaining = nextUnits.filter((u) => u.productId === target.productId).length;
      setProductItems((prevItems) => syncItemForProductCount(prevItems, target, remaining));
      return nextUnits;
    });
  }, []);

  /** Quita un producto completo (todas sus piezas). El caller debe confirmar antes de llamarla. */
  const removeSection = useCallback((productId: number) => {
    setProductUnits((prevUnits) => {
      const nextUnits = prevUnits.filter((u) => u.productId !== productId);
      setProductItems((prevItems) => prevItems.filter((item) => item.productId !== productId));
      return nextUnits;
    });
  }, []);

  /**
   * Absolute section size (header input). Applies against live state after any catalog fetch
   * so overlapping calls converge on the last absolute target, not a stale snapshot length.
   * Minimum 1 — wiping a section uses removeSection (with confirm).
   */
  const setSectionQuantity = useCallback(
    async (productId: number, newQuantity: number) => {
      const safeQuantity = Math.max(1, Math.floor(newQuantity));
      const peek = productUnitsRef.current.find((u) => u.productId === productId);
      if (peek == null) return;

      const fullProduct = await ensureFullProductData(productId, {
        name: peek.productName,
        code: peek.productCode,
      });

      setProductUnits((prevUnits) => {
        const sectionUnits = prevUnits.filter((u) => u.productId === productId);
        if (sectionUnits.length === 0) return prevUnits;
        if (safeQuantity === sectionUnits.length) return prevUnits;

        let nextSection: typeof sectionUnits;
        if (safeQuantity > sectionUnits.length) {
          const reference = sectionUnits[0];
          const added = createDefaultUnitsForProduct({
            productId: reference.productId,
            productName: reference.productName,
            productCode: reference.productCode,
            quantity: safeQuantity - sectionUnits.length,
            product: fullProduct,
            selectedSupplierId,
          });
          nextSection = [...sectionUnits, ...added];
        } else {
          nextSection = sectionUnits.slice(0, safeQuantity);
        }

        const reference = nextSection[0] ?? sectionUnits[0];
        setProductItems((prevItems) =>
          syncItemForProductCount(prevItems, reference, nextSection.length),
        );
        return replaceSectionUnitsInPlace(prevUnits, productId, nextSection);
      });
    },
    [selectedSupplierId, ensureFullProductData],
  );

  /**
   * ±1 from header buttons. Delta is applied on live section length inside setState so rapid
   * clicks do not both target the same absolute count from a stale render snapshot.
   */
  const adjustSectionQuantity = useCallback(
    async (productId: number, delta: number) => {
      if (delta === 0) return;
      const peek = productUnitsRef.current.find((u) => u.productId === productId);
      if (peek == null) return;

      let fullProduct: ProductData | undefined;
      if (delta > 0) {
        fullProduct = await ensureFullProductData(productId, {
          name: peek.productName,
          code: peek.productCode,
        });
      }

      setProductUnits((prevUnits) => {
        const sectionUnits = prevUnits.filter((u) => u.productId === productId);
        if (sectionUnits.length === 0) return prevUnits;

        const target = Math.max(1, sectionUnits.length + delta);
        if (target === sectionUnits.length) return prevUnits;

        let nextSection: typeof sectionUnits;
        if (target > sectionUnits.length) {
          const reference = sectionUnits[0];
          const added = createDefaultUnitsForProduct({
            productId: reference.productId,
            productName: reference.productName,
            productCode: reference.productCode,
            quantity: target - sectionUnits.length,
            product: fullProduct,
            selectedSupplierId,
          });
          nextSection = [...sectionUnits, ...added];
        } else {
          nextSection = sectionUnits.slice(0, target);
        }

        const reference = nextSection[0] ?? sectionUnits[0];
        setProductItems((prevItems) =>
          syncItemForProductCount(prevItems, reference, nextSection.length),
        );
        return replaceSectionUnitsInPlace(prevUnits, productId, nextSection);
      });
    },
    [selectedSupplierId, ensureFullProductData],
  );

  /** Mueve un producto completo un lugar arriba (-1) o abajo (+1). El orden se guarda con el registro. */
  const moveSection = useCallback((productId: number, direction: -1 | 1) => {
    setProductUnits((prevUnits) => {
      const nextUnits = moveSectionUnits(prevUnits, productId, direction);
      if (nextUnits === prevUnits) return prevUnits;
      const order = productOrderFromUnits(nextUnits);
      setProductItems((prevItems) => sortItemsByProductOrder(prevItems, order));
      return nextUnits;
    });
  }, []);

  /** Edita uno o más campos (properties o costs) de una pieza puntual. */
  const updateUnitField = useCallback(
    (unitId: string, section: 'properties' | 'costs', patch: Record<string, string>) => {
      setProductUnits((prevUnits) =>
        prevUnits.map((unit) =>
          unit.id === unitId ? { ...unit, [section]: { ...unit[section], ...patch } } : unit,
        ),
      );
    },
    [],
  );

  /** Restablece todas las piezas de una sección a los valores por defecto del catálogo. */
  const resetSectionToDefaults = useCallback(
    async (productId: number) => {
      const sectionUnits = productUnits.filter((u) => u.productId === productId);
      if (sectionUnits.length === 0) return;
      const reference = sectionUnits[0];
      const product = await ensureFullProductData(reference.productId, {
        name: reference.productName,
        code: reference.productCode,
      });

      setProductUnits((prevUnits) =>
        prevUnits.map((unit) => {
          if (unit.productId !== productId) return unit;
          return {
            ...unit,
            properties: {
              supplierId: selectedSupplierId ?? '',
              materialId: product.material?.id != null ? product.material.id.toString() : '',
              materialName: product.material?.name ?? '',
              weight: product.weightGrams != null ? String(product.weightGrams) : '',
              length: lengthMmToCmField(product.lengthMm),
              diameter: product.diameterSize != null ? String(product.diameterSize) : '',
              color: product.color ?? '',
              workshopCode: product.workshopCode ?? '',
            },
            costs: {
              costPrice: product.costPrice != null ? String(product.costPrice) : '',
              salePrice: product.salePrice != null ? String(product.salePrice) : '',
            },
          };
        }),
      );
    },
    [productUnits, selectedSupplierId, ensureFullProductData],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const selectedIds = Array.from(
      new Set(
        productItems
          .map((item) => {
            const n = Number(item.productId);
            return Number.isInteger(n) && n > 0 ? n : null;
          })
          .filter((id): id is number => id != null),
      ),
    );

    if (selectedIds.length === 0) {
      return;
    }

    const existingIds = new Set(productsData.map((product) => product.id));
    const missingIds = selectedIds.filter(
      (id) => !existingIds.has(id) && !fetchedDefaultsIdsRef.current.has(id),
    );

    if (missingIds.length === 0) {
      return;
    }

    missingIds.forEach((id) => fetchedDefaultsIdsRef.current.add(id));
    void fetchMissingProductDefaults(missingIds);
  }, [open, productItems, productsData, fetchMissingProductDefaults]);

  // Prefetch catalog defaults for every product on the record (step 2 / restablecer), not only form rows.
  useEffect(() => {
    if (!open || !editMode || recordToEdit?.items == null || !Array.isArray(recordToEdit.items)) {
      return;
    }

    const recordProductIds = extractProductIdsFromRecordItems(
      recordToEdit.items as RecordItemLike[],
    );
    if (recordProductIds.length === 0) {
      return;
    }

    const existingIds = new Set(productsData.map((product) => product.id));
    const missingIds = recordProductIds.filter(
      (id) => !existingIds.has(id) && !fetchedDefaultsIdsRef.current.has(id),
    );

    if (missingIds.length === 0) {
      return;
    }

    missingIds.forEach((id) => fetchedDefaultsIdsRef.current.add(id));
    void fetchMissingProductDefaults(missingIds);
  }, [open, editMode, recordToEdit, productsData, fetchMissingProductDefaults]);

  // Validación liviana: lo mínimo para guardar un BORRADOR (proveedor + al menos un producto).
  // Un borrador es justamente trabajo incompleto — nunca debe exigir costos cargados.
  const validateDraftSave = useCallback(() => {
    const hasSupplier = selectedSupplierId != null && selectedSupplierId !== '';
    const hasProducts = productItems.length > 0 && productUnits.length > 0;
    return hasSupplier && hasProducts;
  }, [selectedSupplierId, productItems, productUnits]);

  // Validación completa: además exige costos cargados. Solo para avanzar a Confirmación/finalizar.
  const validateItemsStep = useCallback(() => {
    const hasValidCosts = productUnits.every(
      (unit) => unit.costs && unit.costs.costPrice && parseFloat(unit.costs.costPrice) > 0,
    );
    return validateDraftSave() && hasValidCosts;
  }, [validateDraftSave, productUnits]);

  // Handlers for step navigation (2 pasos: 1 = pantalla unificada, 2 = confirmación)
  const handleNextStep = useCallback(() => {
    if (currentStep === 1) {
      if (!validateItemsStep()) {
        return;
      }
      setCurrentStep(2);
      return;
    }
    setConfirmMessage('¿Confirmar registro?');
    setShowConfirmModal(true);
  }, [currentStep, validateItemsStep]);

  const handlePreviousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  // Helper function to extract numeric values from strings
  const extractNumericValue = (value: string, unit: string): number | undefined => {
    if (value == null || value.trim() === '') return undefined;

    // Remove the unit and any non-numeric characters except decimal point
    const numericString = value.replace(new RegExp(unit, 'gi'), '').replace(/[^\d.]/g, '');
    const numericValue = parseFloat(numericString);

    return isNaN(numericValue) ? undefined : numericValue;
  };

  // productItems y productUnits ya están sincronizados por construcción (todo cambio de cantidad
  // pasa por addProductToDraft/removeUnit/removeSection/setSectionQuantity). Este filtro es solo
  // una defensa ante datos legados (p. ej. un borrador cargado con el flujo anterior).
  const buildUnitsPayloadForItems = useCallback(() => {
    return filterProductUnitsToProductItems(productUnits, productItems);
  }, [productUnits, productItems]);

  // Helper function to transform frontend unit data to backend format
  const transformUnitsToBackendFormat = (units: any[]): InventoryRecordItemDto[] => {
    const supplierIdNum = parseInt(selectedSupplierId, 10);
    const hasValidSupplierId = !isNaN(supplierIdNum) && supplierIdNum > 0;

    const normalizeProductId = (raw: unknown): number | undefined => {
      if (raw === undefined || raw === null || raw === '') return undefined;
      const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (Number.isNaN(n) || n <= 0) return undefined;
      return n;
    };

    // Group by stable catalog identity (productId). Never by display name.
    const unitsByProductId = new Map<number, any[]>();
    for (const unit of units) {
      const productId =
        normalizeProductId(unit.productId) ??
        normalizeProductId(productsData.find((p: any) => p.code === unit.productCode)?.id);

      if (productId == null) {
        throw new Error(
          `Product ID missing for unit (code=${unit.productCode ?? '?'}, name=${unit.productName ?? '?'})`,
        );
      }

      const list = unitsByProductId.get(productId) ?? [];
      list.push(unit);
      unitsByProductId.set(productId, list);
    }

    // Edit mode: map existing record item ids by productId (fallback: productCode)
    const productIdToItemId = new Map<number, number>();
    for (const item of productItems) {
      if (!item.originalItemId) continue;
      const pid = normalizeProductId(item.productId);
      if (pid != null) {
        productIdToItemId.set(pid, item.originalItemId);
        continue;
      }
      const byCode = productsData.find((p: any) => p.code === item.productCode);
      if (byCode?.id) {
        productIdToItemId.set(byCode.id, item.originalItemId);
      }
    }

    return Array.from(unitsByProductId.entries()).map(([productId, productUnits]) => {
      const typedProductUnits = productUnits as any[];

      const individualUnits: IndividualWarehouseUnitDto[] = typedProductUnits.map((unit: any) => {
        const individualUnit: IndividualWarehouseUnitDto = {
          supplierId: hasValidSupplierId ? supplierIdNum : undefined,
          weightGrams: extractNumericValue(unit.properties.weight, 'g'),
          lengthMm: (() => {
            const lengthCm = extractNumericValue(unit.properties.length, 'cm');
            return lengthCm != null ? lengthCm * 10 : undefined;
          })(),
          diameterSize: unit.properties.diameter || undefined,
          color: unit.properties.color || undefined,
          workshopCode: unit.properties.workshopCode || undefined,
          materialId: unit.properties.materialId ? parseInt(unit.properties.materialId) : undefined,
          costPrice: unit.costs.costPrice ? parseFloat(unit.costs.costPrice) : undefined,
          // Empty / invalid → 0 (explicit; do not omit so BE does not inherit catalog)
          salePrice: (() => {
            const raw = String(unit.costs.salePrice ?? '').trim();
            if (raw === '') return 0;
            const n = parseFloat(raw);
            return Number.isFinite(n) && n >= 0 ? n : 0;
          })(),
          condition: 'Excellent', // Default condition
          itemNotes: undefined, // Optional field
        };

        return individualUnit;
      });

      const itemId =
        editMode && productIdToItemId.has(productId) ? productIdToItemId.get(productId) : undefined;
      const item: InventoryRecordItemDto = {
        // Backend accepts optional id - use 0 for new items (backend treats falsy id as new item)
        id: itemId ?? 0,
        productId,
        quantity: typedProductUnits.length,
        individualUnits: individualUnits,
        delete: false,
      };

      return item;
    });
  };

  /** Resolve catalog productId for step-1 rows without relying on non-unique names. */
  const resolveProductIdFromItem = (item: {
    productId?: number | string;
    productCode?: string;
    productName?: string;
  }): number | null => {
    const raw = item.productId;
    if (raw !== undefined && raw !== null && raw !== '') {
      const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (!Number.isNaN(n) && n > 0) return n;
    }
    if (item.productCode) {
      const byCode = productsData.find((p: any) => p.code === item.productCode);
      if (byCode?.id) return byCode.id;
    }
    return null;
  };

  /** Map step-1 rows to API items; fail loudly if any row lacks a stable productId. */
  const mapProductItemsForSave = (): Array<{
    productId: number;
    quantity: number;
    id?: number;
  }> => {
    const unresolvedLabels: string[] = [];
    const mapped = productItems.map((item) => {
      const productId = resolveProductIdFromItem(item);
      if (productId == null) {
        const label =
          (item.productCode != null && String(item.productCode).trim() !== ''
            ? String(item.productCode).trim()
            : null) ??
          (item.productName != null && String(item.productName).trim() !== ''
            ? String(item.productName).trim()
            : null) ??
          'desconocido';
        unresolvedLabels.push(label);
        return null;
      }

      const result: { productId: number; quantity: number; id?: number } = {
        productId,
        quantity: parseInt(item.quantity, 10),
      };

      if (item.originalItemId) {
        result.id = item.originalItemId;
      }

      return result;
    });

    if (unresolvedLabels.length > 0) {
      throw new Error(
        `No se pudo resolver el ID de producto para: ${unresolvedLabels.join(', ')}. Seleccione el producto desde el catálogo.`,
      );
    }

    const productsForInventory = mapped.filter(
      (item): item is { productId: number; quantity: number; id?: number } => item != null,
    );

    if (productsForInventory.length === 0) {
      throw new Error('No valid products selected');
    }

    return productsForInventory;
  };

  /**
   * Al editar, añade entradas { id, delete: true } para cada línea de detalle
   * que existía al cargar y ya no figura en el payload.
   */
  const buildItemsWithDeletions = (lineItems: any[]): any[] => {
    if (!editMode || !recordToEdit) {
      return lineItems;
    }
    const kept = new Set<number>();
    for (const row of lineItems) {
      const iid = row?.id;
      if (iid != null && Number(iid) > 0) {
        kept.add(Number(iid));
      }
    }
    const out = [...lineItems];
    for (const oldId of editLoadedRecordItemIdsRef.current) {
      if (!kept.has(oldId) && editHydratedRecordItemIdsRef.current.has(oldId)) {
        out.push({ id: oldId, delete: true });
      }
    }
    editLoadedRecordItemIdsRef.current = kept;
    return out;
  };

  // Show confirmation modal with message
  const startConfirmation = () => {
    setConfirmMessage(
      `¿Estás seguro de que deseas ${editMode ? 'actualizar' : 'confirmar'} este registro?`,
    );
  };

  // Execute the confirmation action (single-flight: no duplicate COMPLETED records)
  const executeConfirmation = async () => {
    if (confirmingRef.current || isConfirming || isSavingDraft) {
      return;
    }

    // Make sure we have a user ID, otherwise show an error
    if (!currentUser || !currentUser.id) {
      console.error('No user is authenticated');
      setConfirmMessage('No hay usuario autenticado. Inicie sesión de nuevo.');
      return;
    }

    // Validate supplier is selected
    if (selectedSupplierId == null || selectedSupplierId === '') {
      console.error('No supplier selected');
      setConfirmMessage('Error: Debe seleccionar un proveedor para continuar.');
      return;
    }

    const supplierIdNum = parseInt(selectedSupplierId, 10);
    if (isNaN(supplierIdNum) || supplierIdNum <= 0) {
      console.error('Invalid supplier ID');
      setConfirmMessage('Error: Debe seleccionar un proveedor válido para continuar.');
      return;
    }

    const destinationLocationId = parseInt(selectedLocationId, 10);
    if (isNaN(destinationLocationId) || destinationLocationId <= 0) {
      console.error('No valid destination warehouse selected');
      setConfirmMessage('Error: Debe seleccionar un almacén válido para continuar.');
      return;
    }

    confirmingRef.current = true;
    setIsConfirming(true);

    try {
      const unitsForSave = buildUnitsPayloadForItems();
      // Check if we have individual unit data (alineado con filas del paso 1)
      if (unitsForSave.length > 0) {
        // Use the new endpoint with individual units
        // For updates, don't include recordCode (backend doesn't allow it)
        const baseItems = transformUnitsToBackendFormat(unitsForSave);
        const inventoryData: CreateInventoryRecordWithItemsDto | any = {
          destinationLocationId,
          supplierId: supplierIdNum,
          status: 'COMPLETED' as any,
          items: buildItemsWithDeletions(baseItems),
        };

        // IMPORTANT FIX: Only use the Redux mutation if onSave is NOT provided
        if (!onSave) {
          if (editMode && recordToEdit) {
            // Use update endpoint for edits
            await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
              recordToEdit.id.toString(),
              inventoryData,
            );
          } else {
            // Use create endpoint for new records
            await createInventoryRecordWithItems(inventoryData).unwrap();
          }
        } else {
          const dataForParent = {
            ...inventoryData,
            branch_id: selectedLocationId,
            number: editMode && recordToEdit ? recordToEdit.number : undefined,
            id: editMode && recordToEdit ? recordToEdit.id : undefined,
          };
          await onSave(dataForParent);
        }
      } else {
        // Fallback to old method if no individual units
        const inventoryData = {
          destinationLocationId,
          supplierId: supplierIdNum,
          status: 'COMPLETED' as const,
        };

        // Map product items to the format expected by the API
        const productsForInventory = mapProductItemsForSave();

        const itemsForSave = buildItemsWithDeletions(productsForInventory);
        const payloadNoUnits = {
          ...inventoryData,
          items: itemsForSave,
        };

        if (!onSave) {
          if (editMode && recordToEdit) {
            await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
              recordToEdit.id.toString(),
              payloadNoUnits as any,
            );
          } else {
            await createInventoryRecord(payloadNoUnits).unwrap();
          }
        } else {
          const dataForParent = {
            ...inventoryData,
            branch_id: selectedLocationId,
            items: itemsForSave,
            number: editMode && recordToEdit ? recordToEdit.number : undefined,
            id: editMode && recordToEdit ? recordToEdit.id : undefined,
          };
          await onSave(dataForParent);
        }
      }

      // Close only after successful save
      setShowConfirmModal(false);
      setConfirmMessage('');
      onClose();
    } catch (error) {
      console.error(`Failed to ${editMode ? 'update' : 'create'} inventory record:`, error);
      setConfirmMessage(
        `No se pudo ${editMode ? 'actualizar' : 'crear'} el registro. Intente de nuevo.`,
      );
    } finally {
      confirmingRef.current = false;
      setIsConfirming(false);
    }
  };

  const resetConfirmation = () => {
    if (confirmingRef.current || isConfirming) {
      return;
    }
    setShowConfirmModal(false);
    setConfirmMessage('');
  };

  // Handle close click (show cancel confirmation)
  const handleCloseClick = useCallback(() => {
    if (confirmingRef.current || isConfirming || savingDraftRef.current || isSavingDraft) {
      return;
    }
    setShowCancelConfirmation(true);
  }, [isConfirming, isSavingDraft]);

  // Cancel operation (close panel)
  const cancelOperation = useCallback(() => {
    if (confirmingRef.current || isConfirming || savingDraftRef.current || isSavingDraft) {
      return;
    }
    setShowCancelConfirmation(false);
    onClose();
  }, [onClose, isConfirming, isSavingDraft]);

  // Open save draft confirmation modal (called when user clicks "Guardar Borrador")
  const handleOpenSaveDraftConfirm = useCallback(() => {
    if (confirmingRef.current || isConfirming || savingDraftRef.current || isSavingDraft) {
      return;
    }
    if (currentStep === 1) {
      if (!validateDraftSave()) {
        const hasSupplier = Boolean(selectedSupplierId && selectedSupplierId !== '');
        if (!hasSupplier) {
          setShowSupplierValidation(true);
        }
        setSnackbarMessage('Agregue al menos un producto y seleccione proveedor.');
        setSnackbarOpen(true);
        return;
      }
    }
    setShowSaveDraftConfirmModal(true);
  }, [currentStep, validateDraftSave, selectedSupplierId, isConfirming, isSavingDraft]);

  // Handle save draft (saves with PENDING status - called after user confirms in modal)
  const handleSaveDraft = async () => {
    // Make sure we have a user ID
    if (!currentUser || !currentUser.id) {
      console.error('No user is authenticated');
      setSnackbarMessage('No hay usuario autenticado. Inicie sesión de nuevo.');
      setSnackbarOpen(true);
      return;
    }

    // Validate supplier is selected
    if (selectedSupplierId == null || selectedSupplierId === '') {
      console.error('No supplier selected');
      setSnackbarMessage('Error: Debe seleccionar un proveedor para guardar el borrador.');
      setSnackbarOpen(true);
      return;
    }

    const supplierIdNum = parseInt(selectedSupplierId, 10);
    if (isNaN(supplierIdNum) || supplierIdNum <= 0) {
      console.error('Invalid supplier ID');
      setSnackbarMessage('Error: Debe seleccionar un proveedor válido para guardar el borrador.');
      setSnackbarOpen(true);
      return;
    }

    const destinationLocationId = parseInt(selectedLocationId, 10);
    if (isNaN(destinationLocationId) || destinationLocationId <= 0) {
      console.error('No valid destination warehouse selected');
      setSnackbarMessage('Error: Debe seleccionar un almacén válido para guardar el borrador.');
      setSnackbarOpen(true);
      return;
    }

    try {
      const unitsForDraft = buildUnitsPayloadForItems();
      // Check if we have individual unit data (alineado con filas del paso 1)
      if (unitsForDraft.length > 0) {
        // Use the new endpoint with individual units
        // For updates, don't include recordCode (backend doesn't allow it)
        const baseItemsDraft = transformUnitsToBackendFormat(unitsForDraft);
        const inventoryData: CreateInventoryRecordWithItemsDto | any = {
          destinationLocationId,
          supplierId: supplierIdNum,
          status: 'PENDING' as any,
          items: buildItemsWithDeletions(baseItemsDraft),
        };

        // IMPORTANT FIX: Only use the Redux mutation if onSave is NOT provided
        if (!onSave) {
          if (editMode && recordToEdit) {
            // Use update endpoint for edits
            await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
              recordToEdit.id.toString(),
              inventoryData,
            );
          } else {
            // Use create endpoint for new records
            await createInventoryRecordWithItems(inventoryData).unwrap();
          }
        } else {
          const dataForParent = {
            ...inventoryData,
            branch_id: selectedLocationId,
            number: editMode && recordToEdit ? recordToEdit.number : undefined,
            id: editMode && recordToEdit ? recordToEdit.id : undefined,
          };
          await onSave(dataForParent);
        }
      } else {
        // Fallback to old method if no individual units
        // supplierId is required in parent handleSaveRecord → createWithItems / updateWithItems
        // or the record is stored without a supplier and will not show on re-edit
        const inventoryData = {
          destinationLocationId,
          supplierId: supplierIdNum,
          status: 'PENDING' as const, // Save as PENDING
        };

        // Map product items to the format expected by the API
        const productsForInventory = mapProductItemsForSave();

        const itemsForSaveDraft = buildItemsWithDeletions(productsForInventory);
        const payloadDraftNoUnits = {
          ...inventoryData,
          items: itemsForSaveDraft,
        };

        if (!onSave) {
          if (editMode && recordToEdit) {
            await InventoryRecordsService.inventoryRecordsControllerUpdateWithItems(
              recordToEdit.id.toString(),
              payloadDraftNoUnits as any,
            );
          } else {
            await createInventoryRecord(payloadDraftNoUnits).unwrap();
          }
        } else {
          const dataForParent = {
            ...inventoryData,
            branch_id: selectedLocationId,
            items: itemsForSaveDraft,
            number: editMode && recordToEdit ? recordToEdit.number : undefined,
            id: editMode && recordToEdit ? recordToEdit.id : undefined,
          };
          await onSave(dataForParent);
        }
      }

      setShowSaveDraftConfirmModal(false);
      onClose();
    } catch (error) {
      console.error(`Failed to ${editMode ? 'update' : 'save'} draft:`, error);
      setSnackbarMessage(
        `No se pudo ${editMode ? 'actualizar' : 'guardar'} el borrador. Intente de nuevo.`,
      );
      setSnackbarOpen(true);
      throw error;
    }
  };

  // User confirmed save draft in modal - keep modal open with loading until save finishes
  const handleConfirmSaveDraft = async () => {
    if (savingDraftRef.current || isSavingDraft || isConfirming) {
      return;
    }
    savingDraftRef.current = true;
    setIsSavingDraft(true);
    try {
      await handleSaveDraft();
    } catch {
      // Error snackbar already set in handleSaveDraft; keep draft modal open for retry
    } finally {
      savingDraftRef.current = false;
      setIsSavingDraft(false);
    }
  };

  const handleCancelSaveDraftConfirm = useCallback(() => {
    if (savingDraftRef.current || isSavingDraft) {
      return;
    }
    setShowSaveDraftConfirmModal(false);
  }, [isSavingDraft]);

  // Handler for branch/location change
  const handleLocationChange = useCallback((value: string) => {
    setSelectedLocationId(value);
  }, []);

  return {
    // State
    currentUser,
    locationsData,
    locationsLoading,
    locationsError,
    productsData,
    productsLoading,
    productsError,
    currentStep,
    productItems,
    selectedLocationId,
    selectedSupplierId,
    showSupplierValidation,
    handleSupplierChange,
    showConfirmModal,
    confirmMessage,
    showSaveDraftConfirmModal,
    snackbarOpen,
    snackbarMessage,
    setSnackbarOpen,
    productUnits,
    isSubmitting,
    isSubmittingWithItems,
    isConfirming,
    isSavingDraft,
    isSuccess,
    submitError,
    submitErrorWithItems,

    // Actions — pantalla unificada de productos + piezas
    addProductToDraft,
    removeUnit,
    removeSection,
    setSectionQuantity,
    adjustSectionQuantity,
    moveSection,
    updateUnitField,
    resetSectionToDefaults,
    handleNextStep,
    handlePreviousStep,
    validateItemsStep,
    startConfirmation,
    executeConfirmation,
    resetConfirmation,
    handleLocationChange,
    resetForm,
    handleOpenSaveDraftConfirm,
    handleConfirmSaveDraft,
    handleCancelSaveDraftConfirm,

    // Close confirmation
    showCancelConfirmation,
    setShowCancelConfirmation,
    handleCloseClick,
    cancelOperation,
  };
};
